import { useCallback, useEffect, useMemo, useState } from 'react'
import { config } from '../appConfig'
import CacheClient from '../clients/CacheClient'
import { PLUG_REGISTRY_CANISTER_ID } from '../constants/canisterIds'
import {
  idlFactory,
  Link,
  _SERVICE as PlugTokenRegistryFactory,
} from '../idls/plugTokenRegistry/plugTokenRegistry.did'
import { PlugToken } from '../interfaces/common'
import { logger } from '../logger'
import { TokenBalance } from '../providers/BalanceProvider'
import { createActor } from '../utils/createActor'
import useBalances from './useBalances'
import useConfig from './useConfig'

interface TradedTokens {
  tokens: {
    volume: number
    token: PlugToken
  }[]
  chain: string
}

interface SearchResults {
  tokens: TokenBalance[]
  dapps: Link[]
  links: Link[]
}

const queryLink = (link: Link, query: string) =>
  link.title.toLowerCase().includes(query) ||
  (query.length > 3 && link.description[0]?.toLowerCase().includes(query))

const queryToken = (token: PlugToken, query: string) =>
  token.address?.toLowerCase().startsWith(query) ||
  token.name.toLowerCase().startsWith(query) ||
  token.symbol.toLowerCase().startsWith(query)

export const useDiscoverData = () => {
  const [topTradedTokens, setTopTradedTokens] = useState<TradedTokens[]>([])
  const [links, setLinks] = useState<Link[]>([])
  const [selectedChain, setSelectedChain] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResults>({
    tokens: [],
    dapps: [],
    links: [],
  })

  const { tokensToBalances } = useBalances()
  const { tokens } = useConfig()

  useEffect(() => {
    const getTopTokens = async () => {
      try {
        const topTokens = await new CacheClient().getTopTradedTokens()

        const mappedTokens = topTokens.map((data) => ({
          ...data,
          tokens: data.tokens.map((t) => ({
            volume: t.volume_24h,
            token: tokens.find((pt) => pt.id === t.id)!,
          })),
        }))

        setTopTradedTokens(mappedTokens)
        setSelectedChain(mappedTokens[0].chain)
      } catch (e) {
        logger.error('Failed to fetch top traded tokens', { originalError: e })
      }
    }

    getTopTokens()
  }, [])

  useEffect(() => {
    const actor = createActor<PlugTokenRegistryFactory>(idlFactory, {
      canisterId: PLUG_REGISTRY_CANISTER_ID,
      agent: config.getAgent(),
    })

    actor.get_all_links().then(setLinks).catch(logger.error)
  }, [])

  const searchIndex = useMemo(() => {
    return {
      tokens,
      dapps: links.filter((l) => l.kind === 'dapp'),
      links: links.filter((l) => l.kind === 'article'),
    }
  }, [tokens, links])

  const search = useCallback(
    (query: string) => {
      setSearchTerm(query)

      const normalizedQuery = query.trim().toLowerCase()

      setSearchResults({
        tokens: tokensToBalances(searchIndex.tokens.filter((t) => queryToken(t, normalizedQuery))),
        dapps: searchIndex.dapps.filter((d) => queryLink(d, normalizedQuery)),
        links: searchIndex.links.filter((l) => queryLink(l, normalizedQuery)),
      })
    },
    [searchIndex]
  )

  return {
    topTradedTokens,
    links,
    search,
    searchTerm,
    searchResults,
    selectedChain,
    setSelectedChain,
  }
}
